import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { videos } from '@/mocks/videos';
import FilterBar from '@/components/feature/FilterBar';
import VideoGrid from '@/components/feature/VideoGrid';
import Pagination from '@/components/feature/Pagination';
import { useVideoFilter, defaultFilterState } from '@/hooks/useVideoFilter';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

const PAGE_SIZE = 6;

export default function CategoryPage() {
  const { categoryId = '' } = useParams();
  const category = categories.find((c) => c.id === categoryId);

  const [filters, setFilters] = useState(defaultFilterState);
  const [page, setPage] = useState(1);

  const source = useMemo(() => videos.filter((v) => v.categoryId === categoryId), [categoryId]);
  const subCategories = useMemo(() => {
    const names = Array.from(new Set(source.map((v) => v.subCategory)));
    return names.map((n) => ({ value: n, label: n }));
  }, [source]);

  const filtered = useVideoFilter(source, filters);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = Math.min(page, totalPages);
  const pageVideos = filtered.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  useEffect(() => {
    setPage(1);
    setFilters(defaultFilterState);
  }, [categoryId]);

  const updateFilter = (patch: Partial<typeof filters>) => {
    setFilters((prev) => ({ ...prev, ...patch }));
    setPage(1);
  };

  useDocumentTitle(
    `${category ? category.name : '分类'}视频 - 靠逼视频`,
    category ? `${category.description} 在靠逼视频浏览最新${category.name}视频内容。` : '靠逼视频分类页',
  );

  if (!category) {
    return (
      <div className="w-full px-4 md:px-8 py-20 text-center">
        <span className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 text-foreground-500 text-3xl">
          <i className="ri-question-line"></i>
        </span>
        <h1 className="mt-4 font-heading text-xl font-bold text-foreground-950">该分类不存在</h1>
        <p className="mt-2 text-sm text-foreground-600">你访问的分类可能已被移除，试试从首页重新进入。</p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-2 h-10 px-6 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors whitespace-nowrap"
        >
          <i className="ri-home-4-line"></i> 返回首页
        </Link>
      </div>
    );
  }

  return (
    <div className="w-full px-4 md:px-8 py-6 md:py-10 animate-fade-up">
      <nav className="flex items-center flex-wrap gap-2 text-xs text-foreground-600 mb-5" aria-label="面包屑">
        <Link to="/" className="hover:text-primary-400 transition-colors">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <Link to="/" className="hover:text-primary-400 transition-colors">
          分类
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-900">{category.name}</span>
      </nav>

      <div className="flex items-start gap-4 mb-6 md:mb-8">
        <span className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-lg bg-primary-500/15 text-primary-400 text-2xl md:text-3xl shrink-0">
          <i className={category.icon}></i>
        </span>
        <div>
          <h1 className="font-heading text-xl md:text-3xl font-bold text-foreground-950">
            {category.name}视频
          </h1>
          <p className="mt-1.5 text-sm text-foreground-600 leading-relaxed max-w-2xl">{category.description}</p>
        </div>
      </div>

      <div className="bg-background-100 border border-background-200 rounded-lg p-4 md:p-5 mb-6">
        <FilterBar
          subCategories={subCategories}
          subCategory={filters.subCategory}
          onSubCategory={(v) => updateFilter({ subCategory: v })}
          time={filters.time}
          onTime={(v) => updateFilter({ time: v })}
          duration={filters.duration}
          onDuration={(v) => updateFilter({ duration: v })}
          sort={filters.sort}
          onSort={(v) => updateFilter({ sort: v })}
        />
      </div>

      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-foreground-600">
          共找到 <strong className="text-foreground-950 font-semibold">{filtered.length}</strong> 个视频
        </p>
      </div>

      <VideoGrid
        videos={pageVideos}
        columns="grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
        emptyTitle="没有符合条件的视频"
        emptyText="试试调整筛选条件或切换子分类。"
      />

      <Pagination current={current} total={totalPages} onChange={setPage} />

      {filtered.length === 0 && (
        <div className="text-center mt-6">
          <Link
            to="/"
            className="inline-flex items-center gap-2 h-10 px-6 rounded-md bg-background-100 border border-background-300 text-foreground-900 text-sm font-medium hover:border-primary-400 transition-colors whitespace-nowrap"
          >
            <i className="ri-home-4-line"></i> 返回首页
          </Link>
        </div>
      )}
    </div>
  );
}